import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All status' },
  { value: 'new', label: 'New (contact pending)' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'resolved', label: 'Resolved' },
];

const REASON_LABELS = {
  general: 'General',
  complaint: 'Complaint',
  unclear: 'Unclear message',
  callback: 'Callback request',
  payment: 'Payment',
  feedback: 'Feedback',
};

function hoursSince(dateStr) {
  if (!dateStr) return null;
  const ms = Date.now() - new Date(dateStr).getTime();
  return Math.floor(ms / 3600000);
}

function slaBadge(lastMessageAt, status, needsContact) {
  if (!needsContact || status === 'resolved') return null;
  const h = hoursSince(lastMessageAt);
  if (h == null) return null;
  if (h >= 24) {
    return <span className="badge bg-danger ms-1">24h+ overdue</span>;
  }
  return (
    <span className="badge bg-warning text-dark ms-1">
      {24 - h}h left
    </span>
  );
}

const WhatsAppInboxDashboard = () => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [needsOnly, setNeedsOnly] = useState(true);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadMeta, setThreadMeta] = useState({ customerMessages: 0, messageCount: 0 });
  const [threadLoading, setThreadLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [status, setStatus] = useState('new');
  const [resolvingId, setResolvingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (needsOnly) params.set('needsContact', 'true');

      const [statsRes, listRes] = await Promise.all([
        axios.get(`${config.API_URL}/whatsapp-inbox/admin/stats`),
        axios.get(`${config.API_URL}/whatsapp-inbox/admin/list?${params}`),
      ]);
      setStats(statsRes.data.stats);
      setItems(listRes.data.items || []);
    } catch (err) {
      console.error('WhatsApp inbox fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, needsOnly]);

  useEffect(() => {
    fetchData();
    axios.post(`${config.API_URL}/admin/inbox-seen`, { type: 'whatsapp_inbox' }).catch(() => {});
  }, [fetchData]);

  const openItem = async (item) => {
    setSelected(item);
    setAdminNotes(item.adminNotes || '');
    setStatus(item.status || 'new');
    setThread([]);
    setThreadMeta({ customerMessages: 0, messageCount: 0 });
    setThreadLoading(true);
    try {
      const res = await axios.get(`${config.API_URL}/whatsapp-inbox/admin/${item._id}/thread`);
      setThread(res.data.thread || []);
      setThreadMeta({
        customerMessages: res.data.customerMessages || 0,
        messageCount: res.data.messageCount || (res.data.thread || []).length,
      });
    } catch (err) {
      console.error('Thread load error:', err);
    } finally {
      setThreadLoading(false);
    }
  };

  const displayLastMessage = (row) => {
    const msg = row.lastMessage || '';
    if (!msg || msg === 'Unknown') {
      return (
        <span className="text-muted">
          Purana log — <strong>Manage</strong> par click karke poori chat dekho
        </span>
      );
    }
    return msg;
  };

  const saveItem = async () => {
    if (!selected) return;
    try {
      await axios.patch(`${config.API_URL}/whatsapp-inbox/admin/${selected._id}`, {
        status,
        adminNotes,
        ...(status === 'resolved' ? { needsContact: false } : {}),
      });
      setSelected(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    }
  };

  const markResolved = async (row) => {
    if (!row?._id || resolvingId) return;
    setResolvingId(row._id);
    try {
      await axios.patch(`${config.API_URL}/whatsapp-inbox/admin/${row._id}`, {
        status: 'resolved',
        needsContact: false,
      });
      if (selected?._id === row._id) setSelected(null);
      setItems((prev) =>
        prev
          .map((item) =>
            item._id === row._id ? { ...item, status: 'resolved', needsContact: false } : item
          )
          .filter((item) => !(needsOnly && item._id === row._id))
      );
      setStats((prev) => {
        if (!prev || row.status === 'resolved' || !row.needsContact) return prev;
        const hours = hoursSince(row.lastMessageAt);
        return {
          ...prev,
          needsContact: Math.max(0, (prev.needsContact || 0) - 1),
          newCount: Math.max(0, (prev.newCount || 0) - (row.status === 'new' ? 1 : 0)),
          within24h: Math.max(0, (prev.within24h || 0) - (hours != null && hours < 24 ? 1 : 0)),
          overdueContact: Math.max(0, (prev.overdueContact || 0) - (hours != null && hours >= 24 ? 1 : 0)),
        };
      });
    } catch (err) {
      alert(err.response?.data?.message || 'Could not mark resolved');
    } finally {
      setResolvingId(null);
    }
  };

  const waLink = (phone) => {
    const d = String(phone || '').replace(/\D/g, '').slice(-10);
    return `https://wa.me/91${d}`;
  };

  const telLink = (phone) => {
    const d = String(phone || '').replace(/\D/g, '').slice(-10);
    return `tel:+91${d}`;
  };

  if (loading && !stats) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="whatsapp-inbox-page">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-whatsapp me-2 text-success" />
            WhatsApp Messages
          </h2>
          <p className="text-muted mb-0">
            Customer messages jin par 24 ghante me contact karna hai
          </p>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={fetchData}>
          <i className="bi bi-arrow-clockwise me-1" />
          Refresh
        </button>
      </div>

      {stats && (
        <div className="row g-3 mb-4">
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">Contact pending</div>
                <div className="fs-3 fw-bold text-danger">{stats.needsContact}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100 border-warning">
              <div className="card-body">
                <div className="text-muted small">Within 24h</div>
                <div className="fs-3 fw-bold text-warning">{stats.within24h}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100 border-danger">
              <div className="card-body">
                <div className="text-muted small">Overdue (24h+)</div>
                <div className="fs-3 fw-bold text-danger">{stats.overdueContact}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">New / this week</div>
                <div className="fs-5 fw-bold">
                  {stats.newCount}{' '}
                  <span className="text-muted fw-normal">/ {stats.weekCount} week</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label small mb-1">Status</label>
              <select
                className="form-select form-select-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="needsOnly"
                  checked={needsOnly}
                  onChange={(e) => setNeedsOnly(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="needsOnly">
                  Sirf contact chahiye
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {items.length === 0 ? (
            <p className="text-muted text-center py-5 mb-0">Abhi koi pending message nahi.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Last message</th>
                    <th>Customer</th>
                    <th>Message</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const overdue =
                      row.needsContact &&
                      row.status !== 'resolved' &&
                      hoursSince(row.lastMessageAt) >= 24;
                    return (
                      <tr
                        key={row._id}
                        className={overdue ? 'table-danger' : row.status === 'new' && row.needsContact ? 'table-warning' : ''}
                      >
                        <td className="small text-nowrap">
                          {new Date(row.lastMessageAt).toLocaleString('en-IN')}
                          {slaBadge(row.lastMessageAt, row.status, row.needsContact)}
                        </td>
                        <td>
                          <div className="fw-semibold">{row.customerName || '—'}</div>
                          <div className="small text-muted">{row.phone}</div>
                          {row.messageCount > 1 && (
                            <div className="small text-muted">{row.messageCount} messages</div>
                          )}
                        </td>
                        <td className="small" style={{ maxWidth: 260 }}>
                          {displayLastMessage(row)}
                          {!row.autoReplied && (
                            <div className="text-danger small mt-1">Auto-reply failed</div>
                          )}
                        </td>
                        <td className="small">{REASON_LABELS[row.reason] || row.reason}</td>
                        <td>
                          <span
                            className={`badge ${
                              row.status === 'new'
                                ? 'bg-danger'
                                : row.status === 'contacted'
                                  ? 'bg-warning text-dark'
                                  : 'bg-success'
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <a
                              href={waLink(row.phone)}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-success"
                              title="WhatsApp"
                            >
                              <i className="bi bi-whatsapp" />
                            </a>
                            <a href={telLink(row.phone)} className="btn btn-outline-secondary" title="Call">
                              <i className="bi bi-telephone" />
                            </a>
                            <button
                              type="button"
                              className="btn btn-outline-success"
                              title="Mark resolved"
                              disabled={row.status === 'resolved' || resolvingId === row._id}
                              onClick={() => markResolved(row)}
                            >
                              {resolvingId === row._id ? '…' : 'Resolved'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() => openItem(row)}
                            >
                              Manage
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  WhatsApp — {selected.customerName || selected.phone}
                </h5>
                <button type="button" className="btn-close" onClick={() => setSelected(null)} />
              </div>
              <div className="modal-body">
                <p>
                  <strong>Phone:</strong> {selected.phone}
                </p>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-1 mb-1">
                    <strong>24h chat window</strong>
                    {!threadLoading && thread.length > 0 && (
                      <span className="badge bg-secondary">
                        {threadMeta.customerMessages || thread.filter((t) => t.direction === 'in').length} customer
                        {' · '}
                        {threadMeta.messageCount || thread.length} total
                      </span>
                    )}
                  </div>
                  <p className="small text-muted mb-2">
                    Is customer ki poori recent chat (customer + bot) — problem samajh kar call / apne WhatsApp se
                    reply karo.
                  </p>
                  {threadLoading ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  ) : thread.length === 0 ? (
                    <div className="alert alert-light small mb-0">
                      Is window mein saved chat nahi mili. Agle messages se yahan poori history dikhegi.
                      {selected.lastMessage && selected.lastMessage !== 'Unknown' && (
                        <>
                          <br />
                          <strong>Last:</strong> {selected.lastMessage}
                        </>
                      )}
                    </div>
                  ) : (
                    <div
                      className="border rounded p-2 bg-light"
                      style={{ maxHeight: 420, overflowY: 'auto' }}
                    >
                      {thread.map((t, i) => (
                        <div
                          key={`${t.at}-${i}`}
                          className={`mb-2 d-flex ${t.direction === 'in' ? 'justify-content-start' : 'justify-content-end'}`}
                        >
                          <div
                            className={`px-3 py-2 rounded-3 small ${
                              t.direction === 'in'
                                ? 'bg-white border text-dark'
                                : 'bg-success text-white'
                            }`}
                            style={{ maxWidth: '85%', whiteSpace: 'pre-wrap' }}
                          >
                            <div className="fw-semibold opacity-75" style={{ fontSize: 10 }}>
                              {t.direction === 'in' ? 'Customer' : 'A2ZTak (auto)'}
                              {t.at ? ` · ${new Date(t.at).toLocaleString('en-IN')}` : ''}
                            </div>
                            {t.body}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Admin notes</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Call kiya, issue resolve..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <a href={telLink(selected.phone)} className="btn btn-outline-secondary">
                  Call customer
                </a>
                <a href={waLink(selected.phone)} target="_blank" rel="noreferrer" className="btn btn-success">
                  WhatsApp customer
                </a>
                <button type="button" className="btn btn-primary" onClick={saveItem}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppInboxDashboard;
